import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import SideBarModal from '@/components/Modal/SideBarModal';
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from '@/components/toast/globalToast';
import { ShipmentStatusBadge } from '@/pages/shipments/ShipmentStatusBadge';
import {
  cancelSandboxShipment,
  fetchSandboxWebhookDeliveries,
  getSandboxShipment,
  SANDBOX_NEXT_STATUSES,
  SandboxExchange,
  SandboxReadiness,
  SandboxShipmentDetail as Detail,
  SandboxShipmentStatus,
  SandboxWebhookDelivery,
  retrySandboxWebhook,
  simulateSandboxShipment,
} from '@/services/api/sandbox';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import RequestResponsePanel from './RequestResponsePanel';
import {
  formatTime,
  HAPPY_PATH,
  RESTING_BUTTON,
  statusLabel,
  STEP_LABEL,
  useCooldown,
} from './sandboxUi';
import SandboxTimeline from './SandboxTimeline';
import WebhookDeliveriesPanel, {
  retryResultOf,
  SOFT_REFUSAL,
  WebhookRetryResult,
} from './WebhookDeliveriesPanel';

type DevTab = 'webhooks' | 'request';

const READ_COOLDOWN_S = 10;
const RETRY_COOLDOWN_S = 30;

/** The one failure a sandbox FAILED step carries — the API's default when
 *  no reasonCode is sent (an existing core code). No picker, no
 *  failure-reasons call. */
const SANDBOX_FAILURE = {
  code: 'EC5003',
  message: 'Penerima tidak ada di alamat',
};

/**
 * One sandbox shipment: its data, its timeline (with the webhook each event
 * produced), the requests this page made, and the simulator's action bar —
 * the old portal's /simulation/[id], on the integration API.
 */
const SandboxShipmentDetail: React.FC<{
  id: string;
  readiness: SandboxReadiness | null;
  keyPrefix: string | null;
  /** The active Sandbox endpoint: where every step's webhook goes. */
  endpointUrl?: string | null;
  /** The exchange that led here (a create), shown first. */
  initialExchange: SandboxExchange | null;
  onBack: () => void;
  onSetupChanged: () => void;
  onNavigate?: (tab: 'keys' | 'webhooks') => void;
}> = ({
  id,
  readiness,
  keyPrefix,
  endpointUrl,
  initialExchange,
  onBack,
  onSetupChanged,
  onNavigate,
}) => {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<SandboxWebhookDelivery[] | null>(
    null
  );
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const [exchange, setExchange] = useState<SandboxExchange | null>(
    initialExchange
  );
  const [devTab, setDevTab] = useState<DevTab>(
    initialExchange ? 'request' : 'webhooks'
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // The last manual retry's outcome — shown inside its webhook row until the
  // next retry, so it does not vanish with a toast.
  const [retryResult, setRetryResult] = useState<WebhookRetryResult | null>(
    null
  );
  // Against hammering: Refresh / GET detail rest 10 s after a click (one
  // cooldown for both — they are the same reads); Retry rests 30 s, the
  // API's own per-shipment limit.
  const readCooldown = useCooldown(READ_COOLDOWN_S);
  const retryCooldown = useCooldown(RETRY_COOLDOWN_S);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [failOpen, setFailOpen] = useState(false);

  // The detail is read with the same GET a partner makes. Opening the page
  // and Refresh show that GET in Request & response (`show`); the reload
  // after a step does not, so the step's own request stays on screen.
  const loadDetail = useCallback(
    async ({ show = false } = {}) => {
      try {
        const read = await getSandboxShipment(id);
        if (show) setExchange(read);
        if (read.status >= 400) {
          const body = read.response as { error?: string } | undefined;
          setLoadError(body?.error ?? `HTTP ${read.status}`);
          return;
        }
        setDetail((read.response as { data: Detail }).data);
        setLoadError(null);
      } catch (err) {
        setLoadError(
          extractApiErrorMessage(err, 'Shipment could not be loaded')
        );
      }
    },
    [id]
  );

  // Webhook deliveries are read on open, right after a step and on Refresh —
  // never polled: a queued one usually leaves within seconds, and Refresh
  // shows where it stands.
  const loadDeliveries = useCallback(async () => {
    setDeliveriesLoading(true);
    try {
      setDeliveries(await fetchSandboxWebhookDeliveries(id));
    } catch {
      setDeliveries((prev) => prev ?? null);
    } finally {
      setDeliveriesLoading(false);
    }
  }, [id]);

  // Opened right after a create: that create stays in Request & response.
  const showReadOnOpen = useRef(initialExchange === null);
  useEffect(() => {
    loadDetail({ show: showReadOnOpen.current });
    loadDeliveries();
  }, [loadDetail, loadDeliveries]);

  const run = async (key: string, request: () => Promise<SandboxExchange>) => {
    if (busy) return;
    setBusy(key);
    try {
      const result = await request();
      setExchange(result);
      // Errors carry no code (the HTTP status is the API's signal), so a
      // refused step simply re-reads the setup — a key or endpoint may have
      // gone since the page loaded — and shows what the API answered.
      if (result.status >= 400) {
        onSetupChanged();
        setDevTab('request');
      }
      await loadDetail();
      loadDeliveries();
    } catch (err) {
      showErrorToast(
        'Request failed',
        extractApiErrorMessage(err, 'Could not reach the API')
      );
    } finally {
      setBusy(null);
    }
  };

  // The shipment's latest webhook, sent again now (the API decides what is
  // retryable; the panel only offers it where the API would accept it).
  const retryWebhook = async (rowKey: string) => {
    if (retrying || retryCooldown.left > 0) return;
    setRetrying(true);
    const at = new Date().toISOString();
    try {
      const result = await retrySandboxWebhook(id);
      setExchange(result);
      // The API counts any attempt, and answers 429 within 30 s: rest with it.
      if (result.status < 400 || result.status === 429) retryCooldown.start();
      // Either way the list is re-read: a refusal means the screen was behind
      // (delivered or queued meanwhile, or the endpoint is gone — so the setup
      // is re-read too); the toast keeps the Webhooks tab up.
      loadDeliveries();
      setRetryResult(retryResultOf(result, rowKey, at));
      if (result.status >= 400) {
        if (result.status === 400) onSetupChanged();
        const body = result.response as { error?: string } | undefined;
        const reason = body?.error ?? `HTTP ${result.status}`;
        // 409 and 429 are not failures: the screen was behind (delivered or
        // queued meanwhile), or the webhook was retried a moment ago.
        const notice = SOFT_REFUSAL[result.status];
        if (notice) showWarningToast(notice, reason);
        else showErrorToast('Retry refused', reason);
        return;
      }
      const row = (result.response as { data?: SandboxWebhookDelivery })?.data;
      if (row?.delivery.status === 'DONE') {
        showSuccessToast(
          'Webhook delivered',
          'Your endpoint accepted the latest webhook.'
        );
      } else {
        showWarningToast(
          'Your endpoint failed again',
          row?.delivery.lastError ?? 'See the delivery for the cause.'
        );
      }
    } catch (err) {
      const reason = extractApiErrorMessage(err, 'Could not reach the API');
      setRetryResult({
        rowKey,
        at,
        outcome: 'error',
        title: 'Retry failed',
        detail: reason,
      });
      showErrorToast('Retry failed', reason);
    } finally {
      setRetrying(false);
    }
  };

  if (loadError && !detail) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-6 text-sm text-text-sub-600">
          {loadError}
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} />
        <p className="text-sm text-text-soft-400">Loading shipment…</p>
      </div>
    );
  }

  const next = SANDBOX_NEXT_STATUSES[detail.status] ?? [];
  const ready = readiness?.ready === true;
  const canCancel = detail.status === 'CREATED';

  return (
    <div className="space-y-5 pb-24">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="space-y-3">
        <BackLink onBack={onBack} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="truncate font-mono text-lg font-semibold text-text-strong-950">
                {detail.waybill}
              </h2>
              <ShipmentStatusBadge status={detail.status} />
            </div>
            <p className="mt-1 text-sm text-text-sub-600">
              <span className="font-mono">{detail.bookingId}</span> ·{' '}
              {detail.type} · created {formatTime(detail.createdAt)}
            </p>
          </div>
          <Button
            variant="white"
            size="small"
            disabled={readCooldown.left > 0}
            className={readCooldown.left > 0 ? RESTING_BUTTON : ''}
            onClick={() => {
              readCooldown.start();
              loadDetail({ show: true });
              loadDeliveries();
            }}
          >
            <i className="ri-refresh-line mr-1.5" />
            {readCooldown.left > 0
              ? `Refresh (${readCooldown.left}s)`
              : 'Refresh'}
          </Button>
        </div>
        <StatusTrack detail={detail} />
      </div>

      {/* ── Shipment + timeline ────────────────────────────────── */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
          <h3 className="text-base font-semibold text-text-strong-950">
            Shipment
          </h3>
          <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Field label="Sender">
              {detail.sender.name}
              <Sub>{detail.sender.phone ?? '—'}</Sub>
            </Field>
            <Field label="Recipient">
              {detail.recipient.name}
              <Sub>{detail.recipient.phone ?? '—'}</Sub>
            </Field>
            <Field label="Origin">{detail.origin?.address ?? '—'}</Field>
            <Field label="Destination">{detail.destination.address}</Field>
            <Field label="Courier">
              {detail.courier ? (
                <>
                  {detail.courier.name}
                  <Sub>{detail.courier.code ?? '—'}</Sub>
                </>
              ) : (
                <span className="text-text-sub-600">Not assigned yet</span>
              )}
            </Field>
            <Field label="Total weight">
              <span className="tabular-nums">{detail.totalWeight} kg</span>
            </Field>
          </dl>
          <h4 className="mt-6 text-sm font-semibold text-text-strong-950">
            Items ({detail.items.length})
          </h4>
          <ul className="mt-2 divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
            {detail.items.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <span className="min-w-0 truncate font-mono text-xs text-text-strong-950">
                  {item.name}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-text-sub-600">
                  {item.quantity} × {item.weight} kg ·{' '}
                  {statusLabel(item.status)}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
          <h3 className="text-base font-semibold text-text-strong-950">
            Timeline
          </h3>
          <SandboxTimeline events={detail.timeline} deliveries={deliveries} />
        </section>
      </div>

      {/* ── Developer ──────────────────────────────────────────── */}
      <section className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        <div className="flex flex-wrap items-center justify-between gap-x-3 border-b border-stroke-soft-200 px-5">
          <div role="tablist" aria-label="Developer" className="flex gap-1">
            {(
              [
                [
                  'webhooks',
                  `Webhooks${deliveries ? ` (${deliveries.length})` : ''}`,
                ],
                ['request', 'Request & response'],
              ] as [DevTab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={devTab === key}
                onClick={() => setDevTab(key)}
                className={`-mb-px border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                  devTab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* The partner's own read of this shipment, on demand: the same GET
              opening the page made, shown in Request & response — after a
              step, that tab holds the step's POST instead. */}
          <Button
            variant="white"
            size="small"
            disabled={reading || readCooldown.left > 0}
            className={!reading && readCooldown.left > 0 ? RESTING_BUTTON : ''}
            onClick={async () => {
              readCooldown.start();
              setReading(true);
              setDevTab('request');
              await loadDetail({ show: true });
              setReading(false);
            }}
          >
            <i className="ri-code-s-slash-line mr-1.5" />
            {reading
              ? 'Reading…'
              : readCooldown.left > 0
                ? `GET shipment detail (${readCooldown.left}s)`
                : 'GET shipment detail'}
          </Button>
        </div>
        <div className="p-5">
          {devTab === 'webhooks' ? (
            <WebhookDeliveriesPanel
              deliveries={deliveries}
              loading={deliveriesLoading}
              shipmentStatus={detail?.status}
              retry={{
                onRetry: retryWebhook,
                busy: retrying,
                waitSeconds: retryCooldown.left,
                noEndpoint: readiness?.hasEndpoint === false,
                onAddEndpoint: onNavigate
                  ? () => onNavigate('webhooks')
                  : undefined,
                result: retryResult,
              }}
            />
          ) : (
            <RequestResponsePanel exchange={exchange} keyPrefix={keyPrefix} />
          )}
        </div>
      </section>

      {/* ── Action bar (portal-style) ──────────────────────────── */}
      <div
        className={`${
          // Sticky only while there is something to press.
          next.length > 0 || canCancel ? 'sticky bottom-4 z-10' : ''
        } rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-3 shadow-card-xs sm:px-5`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* No min-w-0: the group keeps its buttons whole, so the step
              buttons wrap to the next line on a phone instead of covering
              them (the hint inside still truncates). */}
          <div className="flex flex-1 items-center gap-3 text-sm">
            {canCancel && (
              <Button
                variant="white"
                size="small"
                disabled={busy !== null}
                className="whitespace-nowrap"
                onClick={() => {
                  setCancelReason('');
                  setCancelOpen(true);
                }}
              >
                <i className="ri-close-circle-line mr-1.5 text-error-base" />
                Cancel shipment
              </Button>
            )}
            {!ready && next.length > 0 ? (
              <span className="text-warning-dark">
                Finish setup to simulate —{' '}
                <button
                  type="button"
                  onClick={() =>
                    onNavigate?.(readiness?.hasKey ? 'webhooks' : 'keys')
                  }
                  className="font-medium underline"
                >
                  {readiness?.hasKey ? 'add a webhook' : 'create a key'}
                </button>
              </span>
            ) : (
              // The endpoint hint is left out on a phone: there it truncates
              // to a lone letter beside the buttons.
              <span
                className={`min-w-0 truncate text-text-sub-600 ${
                  next.length > 0 && endpointUrl ? 'hidden sm:block' : ''
                }`}
              >
                {next.length === 0 ? (
                  'Finished — nothing left to simulate.'
                ) : endpointUrl ? (
                  <>
                    Each step sends a webhook to{' '}
                    <code className="font-mono text-xs text-text-strong-950">
                      {endpointUrl}
                    </code>
                  </>
                ) : (
                  `Now: ${statusLabel(detail.status)}`
                )}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {next.slice(1).map((status) => (
              <Button
                key={status}
                variant="white"
                disabled={!ready || busy !== null}
                onClick={() =>
                  status === 'FAILED'
                    ? setFailOpen(true)
                    : run(`step-${status}`, () =>
                        simulateSandboxShipment(id, status)
                      )
                }
              >
                {STEP_LABEL[status] ?? statusLabel(status)}
              </Button>
            ))}
            {next[0] && (
              <Button
                disabled={!ready || busy !== null}
                onClick={() =>
                  run('step-next', () => simulateSandboxShipment(id))
                }
              >
                {busy === 'step-next'
                  ? 'Moving…'
                  : `Next: ${STEP_LABEL[next[0]] ?? statusLabel(next[0])}`}
                <i className="ri-arrow-right-line ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <SideBarModal
        isOpen={failOpen}
        onClose={() => setFailOpen(false)}
        width="sm"
        className="max-w-[calc(100vw-2rem)]"
        header={
          <div>
            <h2 className="text-lg font-semibold text-text-strong-950">
              Fail this delivery attempt
            </h2>
            <p className="mt-0.5 text-xs text-text-sub-600">
              The FAILED webhook carries one fixed reason.
            </p>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="white" onClick={() => setFailOpen(false)}>
              Back
            </Button>
            <Button
              disabled={busy !== null}
              onClick={async () => {
                setFailOpen(false);
                // No reasonCode: the API fills its default (SANDBOX_FAILURE).
                await run('step-FAILED', () =>
                  simulateSandboxShipment(id, 'FAILED')
                );
              }}
            >
              Fail delivery
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-6">
          <div className="rounded-lg border border-stroke-soft-200 px-3 py-2.5">
            <p className="text-sm text-text-strong-950">
              {SANDBOX_FAILURE.message}
            </p>
            <p className="font-mono text-xs text-text-sub-600">
              {SANDBOX_FAILURE.code}
            </p>
          </div>
          <p className="text-xs text-text-sub-600">
            Sent as <code className="font-mono">event.reason</code> on the
            FAILED webhook.
          </p>
        </div>
      </SideBarModal>

      <SideBarModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        width="sm"
        className="max-w-[calc(100vw-2rem)]"
        header={
          <h2 className="text-lg font-semibold text-text-strong-950">
            Cancel this shipment?
          </h2>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="white" onClick={() => setCancelOpen(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={busy !== null}
              onClick={async () => {
                setCancelOpen(false);
                await run('cancel', () =>
                  cancelSandboxShipment(
                    id,
                    cancelReason.trim() || 'Cancelled from the sandbox'
                  )
                );
              }}
            >
              Cancel shipment
            </Button>
          </div>
        }
      >
        <div className="space-y-3 p-6">
          <p className="text-sm text-text-sub-600">
            Only a CREATED shipment can be cancelled. Its items are cancelled
            too, and a CANCELLED webhook goes to your Sandbox endpoint.
          </p>
          <label
            htmlFor="sbx-cancel-reason"
            className="block text-sm font-medium text-text-strong-950"
          >
            Reason{' '}
            <span className="font-normal text-text-sub-600">(optional)</span>
          </label>
          <textarea
            id="sbx-cancel-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            maxLength={1000}
            rows={3}
            className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none"
          />
        </div>
      </SideBarModal>
    </div>
  );
};

const BackLink: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <button
    type="button"
    onClick={onBack}
    className="inline-flex items-center gap-1 text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
  >
    <i className="ri-arrow-left-line" />
    Sandbox shipments
  </button>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium text-text-sub-600">{label}</dt>
    <dd className="mt-0.5 break-words text-sm text-text-strong-950">
      {children}
    </dd>
  </div>
);

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="block text-xs text-text-sub-600">{children}</span>
);

/** The happy path as a track; a terminal off-path status gets its own chip. */
const StatusTrack: React.FC<{ detail: Detail }> = ({ detail }) => {
  const reached = new Set<SandboxShipmentStatus>(
    detail.timeline.map((t) => t.status)
  );
  const offPath = !HAPPY_PATH.includes(detail.status);
  // Off the happy path (failed, returned, cancelled) the track stops at the
  // last step reached and ends with the actual status, so no unreached step
  // sits before it.
  const lastReached = HAPPY_PATH.reduce(
    (last, step, i) => (reached.has(step) ? i : last),
    0
  );
  const steps = offPath ? HAPPY_PATH.slice(0, lastReached + 1) : HAPPY_PATH;
  return (
    <ol
      aria-label="Status track"
      className="flex flex-wrap items-center gap-x-2 gap-y-2 text-xs"
    >
      {steps.map((step, i) => {
        const current = detail.status === step;
        const done = !current && reached.has(step);
        return (
          <li key={step} className="flex items-center gap-2">
            {i > 0 && (
              <span
                aria-hidden="true"
                className={`h-px w-4 ${done || current ? 'bg-primary' : 'bg-stroke-sub-300'}`}
              />
            )}
            <span
              aria-current={current ? 'step' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
                current
                  ? 'bg-(--primary-alpha-10) text-primary'
                  : done
                    ? 'text-text-strong-950'
                    : 'text-text-soft-400'
              }`}
            >
              {done && <i className="ri-check-line" aria-hidden="true" />}
              {statusLabel(step)}
            </span>
          </li>
        );
      })}
      {offPath && (
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="h-px w-4 bg-primary" />
          <ShipmentStatusBadge status={detail.status} />
        </li>
      )}
    </ol>
  );
};

export default SandboxShipmentDetail;
